import type { GameSettings } from './types';

type SoundName = 'cannon' | 'splash' | 'hit' | 'blink' | 'ui' | 'success' | 'destroyed';

type AudioFormat = 'mp3' | 'ogg';

const AUDIO_FORMAT: AudioFormat = (() => {
  const probe = document.createElement('audio');
  return probe.canPlayType('audio/mpeg') ? 'mp3' : 'ogg';
})();

const assetUrl = (name: string): string => `/assets/audio/${name}.${AUDIO_FORMAT}`;

const SOUND_URLS: Record<SoundName, string> = {
  cannon: assetUrl('cannon-fire'),
  splash: assetUrl('cannon-miss'),
  hit: assetUrl('cannon-hit'),
  blink: assetUrl('magic-open'),
  ui: assetUrl('ui-select'),
  success: assetUrl('ui-confirm'),
  destroyed: assetUrl('ship-destroyed'),
};

/**
 * Small sample-based soundscape. The old prototype generated every effect with
 * oscillators and filtered noise; this intentionally uses authored CC0 audio.
 */
export class ProceduralAudio {
  private readonly ambience = new Audio(assetUrl('oceanwaves'));
  private readonly effects = new Map<SoundName, HTMLAudioElement>();
  private readonly activeVoices = new Set<HTMLAudioElement>();
  private unlocked = false;
  private unlockAttempts = 0;
  private lastError = '';

  constructor(private readonly settings: GameSettings) {
    this.ambience.loop = true;
    this.ambience.preload = 'auto';
    for (const [name, url] of Object.entries(SOUND_URLS) as [SoundName, string][]) {
      const audio = new Audio(url);
      audio.preload = 'auto';
      this.effects.set(name, audio);
    }
    this.updateVolumes();
  }

  async unlock(): Promise<void> {
    this.unlockAttempts += 1;
    this.unlocked = true;
    this.lastError = '';
    this.ambience.muted = false;
    this.updateVolumes();
    try {
      await this.ambience.play();
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : String(error);
    }
  }

  updateVolumes(): void {
    this.ambience.volume = this.clampVolume(this.settings.masterVolume * this.settings.musicVolume * 0.68);
  }

  cannon(): void { this.play('cannon', 0.92, 0.94 + Math.random() * 0.08); }
  splash(): void { this.play('splash', 0.48, 0.9 + Math.random() * 0.14); }
  hit(): void { this.play('hit', 0.76, 0.96 + Math.random() * 0.06); }
  blink(): void { this.play('blink', 0.48, 0.88); }
  ui(): void { this.play('ui', 0.36, 0.86); }
  success(): void { this.play('success', 0.42, 0.82); }
  destroyed(): void { this.play('destroyed', 0.86, 0.9); }

  diagnosticState(): Record<string, string | number | boolean> {
    return {
      unlocked: this.unlocked,
      attempts: this.unlockAttempts,
      ambiencePaused: this.ambience.paused,
      ambienceMuted: this.ambience.muted,
      ambienceTime: Number(this.ambience.currentTime.toFixed(2)),
      ambienceReadyState: this.ambience.readyState,
      ambienceVolume: Number(this.ambience.volume.toFixed(2)),
      activeVoices: this.activeVoices.size,
      format: AUDIO_FORMAT,
      mp3Support: this.ambience.canPlayType('audio/mpeg') || 'no',
      oggSupport: this.ambience.canPlayType('audio/ogg; codecs="vorbis"') || 'no',
      lastError: this.lastError,
    };
  }

  private play(name: SoundName, gain: number, rate: number): void {
    if (!this.unlocked) return;
    const template = this.effects.get(name);
    if (!template) return;
    const voice = template.cloneNode(true) as HTMLAudioElement;
    voice.volume = this.clampVolume(this.settings.masterVolume * this.settings.effectsVolume * gain);
    voice.playbackRate = rate;
    voice.muted = false;
    this.activeVoices.add(voice);
    const release = () => this.activeVoices.delete(voice);
    voice.addEventListener('ended', release, { once: true });
    voice.addEventListener('error', release, { once: true });
    void voice.play().catch((error) => {
      this.lastError = error instanceof Error ? error.message : String(error);
      release();
    });
  }

  private clampVolume(value: number): number {
    return Math.min(1, Math.max(0, value));
  }
}
